import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TuneIcon from '@mui/icons-material/Tune';
import ViewListIcon from '@mui/icons-material/ViewList';

import { ManualConfirmedPair } from '../../types/entry.types';
import { sortGroups } from '../../utils/sortGroups';
import {
  Subgroup,
  useDeleteSubgroup,
  useSubgroups
} from '../../api/useSubgroups';
import {
  SubgroupSelection,
  UNASSIGNED_SUBGROUP_SENTINEL,
  resolveSubgroupSelection
} from './subgroupFilter';
import { SubgroupDriftBadge } from './SubgroupDriftBadge';
import { GroupMakerBulkControls } from './GroupMakerBulkControls';
import { SubgroupConfigDialog } from './SubgroupConfigDialog';
import { CreateSubgroupDialog } from './CreateSubgroupDialog';
import { SubgroupTemplatesDialog } from './SubgroupTemplatesDialog';
import { SubgroupAssignPanel } from './SubgroupAssignPanel';

/**
 * Subgroup rail/selector, rendered INSIDE the currently-selected group on the
 * opener page (only when a single group is selected — subgroups are nested in
 * a group). Mirrors the group `Select` + the BucketPage rail's
 * client-computed counts. Owns the subgroup CRUD / config / template /
 * assignment surfaces. The URL persistence (`?subgroup=`) is owned by
 * EntryPage via `selection` / `onSelectionChange` — this component is the view.
 */

const ALL_SUBGROUPS_VALUE = 'all';

interface Props {
  groupId: string;
  /** Active opener Path tab — drives which 2 of the 4 maker flags the live
   *  per-leg subgroup toggle flips. */
  path: 'a' | 'b';
  /** Pairs already narrowed to the active group by EntryPage. */
  groupedPairs: ManualConfirmedPair[];
  /** URL-resolved subgroup selection (EntryPage owns the param). */
  selection: SubgroupSelection;
  onSelectionChange: (selection: SubgroupSelection) => void;
}

export function SubgroupRail({
  groupId,
  path,
  groupedPairs,
  selection,
  onSelectionChange
}: Props) {
  const { data, isSuccess: subgroupsResolved } = useSubgroups(groupId);
  const subgroups = useMemo(() => data?.subgroups ?? [], [data]);
  const sortedSubgroups = useMemo(() => sortGroups(subgroups), [subgroups]);
  const deleteMutation = useDeleteSubgroup();

  // Dead-subgroup fallback (deleted, or it belongs to a different group).
  const resolved = useMemo(
    () => resolveSubgroupSelection(selection, subgroups, subgroupsResolved),
    [selection, subgroups, subgroupsResolved]
  );

  const [configTarget, setConfigTarget] = useState<Subgroup | null>(null);
  const [editTarget, setEditTarget] = useState<Subgroup | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  // Per-subgroup counts, client-computed from the active group's loaded pairs
  // — same single-source approach as the BucketPage rail.
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    let unassigned = 0;
    for (const p of groupedPairs) {
      if (p.openerSubgroupId == null) unassigned += 1;
      else map.set(p.openerSubgroupId, (map.get(p.openerSubgroupId) ?? 0) + 1);
    }
    return { map, unassigned };
  }, [groupedPairs]);

  const selectValue =
    resolved.kind === 'all'
      ? ALL_SUBGROUPS_VALUE
      : resolved.kind === 'unassigned'
        ? UNASSIGNED_SUBGROUP_SENTINEL
        : resolved.subgroupId;

  const handleChange = (value: string) => {
    if (value === ALL_SUBGROUPS_VALUE) onSelectionChange({ kind: 'all' });
    else if (value === UNASSIGNED_SUBGROUP_SENTINEL)
      onSelectionChange({ kind: 'unassigned' });
    else onSelectionChange({ kind: 'subgroup', subgroupId: value });
  };

  const activeSubgroup =
    resolved.kind === 'subgroup'
      ? subgroups.find(s => s._id === resolved.subgroupId) ?? null
      : null;

  const handleDelete = (subgroup: Subgroup) => {
    if (
      !window.confirm(
        `Delete subgroup "${subgroup.name}"? Its pairs become unassigned; member rules keep their last config.`
      )
    ) {
      return;
    }
    if (activeSubgroup?._id === subgroup._id) onSelectionChange({ kind: 'all' });
    void deleteMutation
      .mutateAsync({ id: subgroup._id, groupId })
      .catch(() => {});
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        pt: 0.5,
        mt: 0.5,
        flexShrink: 0
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        <Typography
          sx={{
            fontSize: '0.62rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: 'grey.500'
          }}
        >
          Subgroups
        </Typography>
        <Select
          size="small"
          value={selectValue}
          onChange={e => handleChange(e.target.value)}
          sx={{
            width: 180,
            height: 28,
            fontSize: '0.75rem',
            '& .MuiSelect-select': { py: 0.5, px: 1 }
          }}
          inputProps={{ 'aria-label': 'Filter pairs by subgroup' }}
        >
          <MenuItem value={ALL_SUBGROUPS_VALUE} sx={{ fontSize: '0.75rem' }}>
            All subgroups
          </MenuItem>
          <MenuItem
            value={UNASSIGNED_SUBGROUP_SENTINEL}
            sx={{ fontSize: '0.75rem' }}
          >
            Unassigned ({counts.unassigned})
          </MenuItem>
          {sortedSubgroups.map(s => (
            <MenuItem key={s._id} value={s._id} sx={{ fontSize: '0.75rem' }}>
              {s.name} ({counts.map.get(s._id) ?? 0})
            </MenuItem>
          ))}
        </Select>

        <Tooltip title="New subgroup">
          <IconButton
            size="small"
            onClick={() => {
              setEditTarget(null);
              setCreateOpen(true);
            }}
          >
            <AddIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>

        {/* Active-subgroup actions: edit identity, edit config + apply,
            drift badge, delete. */}
        {activeSubgroup && (
          <>
            <Tooltip title="Edit subgroup name / color">
              <IconButton
                size="small"
                onClick={() => {
                  setEditTarget(activeSubgroup);
                  setCreateOpen(true);
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: '0.95rem' }} />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              startIcon={<TuneIcon sx={{ fontSize: '0.9rem' }} />}
              onClick={() => setConfigTarget(activeSubgroup)}
              sx={{ fontSize: '0.68rem', py: 0.1 }}
            >
              Config &amp; apply
            </Button>
            <SubgroupDriftBadge subgroup={activeSubgroup} enabled />
            <Tooltip title="Delete subgroup">
              <IconButton
                size="small"
                onClick={() => handleDelete(activeSubgroup)}
                disabled={deleteMutation.isLoading}
              >
                <DeleteOutlineIcon sx={{ fontSize: '0.95rem' }} />
              </IconButton>
            </Tooltip>

            {/* LIVE per-leg maker toggle for THIS subgroup — distinct from
                "Config & apply" (a snapshot stamp). Flips the active Path tab's
                2 maker flags across every rule in the subgroup in one request. */}
            <Box
              sx={{
                width: '1px',
                alignSelf: 'stretch',
                backgroundColor: 'rgba(255,255,255,0.12)',
                mx: 0.4
              }}
            />
            <Typography
              sx={{
                fontSize: '0.62rem',
                color: 'grey.500',
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                whiteSpace: 'nowrap'
              }}
            >
              Apply to {activeSubgroup.name}
            </Typography>
            <GroupMakerBulkControls
              path={path}
              scope={{
                kind: 'subgroup',
                subgroupId: activeSubgroup._id,
                label: activeSubgroup.name
              }}
              confirmCount={counts.map.get(activeSubgroup._id) ?? 0}
            />
          </>
        )}

        <Box sx={{ flex: 1 }} />

        <Button
          size="small"
          startIcon={<ViewListIcon sx={{ fontSize: '0.9rem' }} />}
          onClick={() => setAssignOpen(o => !o)}
          sx={{ fontSize: '0.68rem', py: 0.1, color: 'grey.400' }}
        >
          {assignOpen ? 'Hide assign' : 'Assign pairs'}
        </Button>
        <Button
          size="small"
          onClick={() => setTemplatesOpen(true)}
          sx={{ fontSize: '0.68rem', py: 0.1, color: 'grey.400' }}
        >
          Templates
        </Button>
      </Stack>

      <Collapse in={assignOpen} unmountOnExit>
        <Box sx={{ mt: 0.5 }}>
          <SubgroupAssignPanel
            groupId={groupId}
            pairs={groupedPairs}
            subgroups={subgroups}
          />
        </Box>
      </Collapse>

      <SubgroupConfigDialog
        open={configTarget !== null}
        subgroup={configTarget}
        onClose={() => setConfigTarget(null)}
      />
      <CreateSubgroupDialog
        open={createOpen}
        groupId={groupId}
        subgroup={editTarget}
        onClose={() => setCreateOpen(false)}
      />
      <SubgroupTemplatesDialog
        open={templatesOpen}
        groupId={groupId}
        onClose={() => setTemplatesOpen(false)}
      />
    </Box>
  );
}
